import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Car, Banknote, MapPin } from 'lucide-react';
import { scrollToCTAEvent } from './CarForm';

export const HowItWorks: React.FC = () => {
  const regions = [
    'Adelaide CBD',
    'Eastern Suburbs',
    'Western Suburbs',
    'Southern Suburbs',
    'Northern Suburbs',
    'Adelaide Hills',
    'Fleurieu Peninsula'
  ];

  return (
    <section className="relative py-20 overflow-hidden bg-gray-50">
      {/* Background Accent */}
      <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-1/2 -translate-y-1/2">
        <img src="/img/accent1.png" alt="accent" className="w-full h-full opacity-50" />
      </div>

      <div className="container px-4 mx-auto">
        <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
              <img
                src="/img/abour-sect.jpg"
                alt="How It Works"
                className="object-cover w-full h-full"
              />
              {/* Overlay Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="absolute bottom-0 left-0 right-0 p-6 text-white bg-gradient-to-t from-black/80"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-3xl font-bold">10k+</h4>
                    <p className="text-sm">Satisfied Customers</p>
                  </div>
                  <div>
                    <h4 className="text-3xl font-bold">7</h4>
                    <p className="text-sm">Regions Covered</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h2 className="mb-4 text-3xl font-bold">How Does It Work?</h2>
              <p className="mb-6 text-xl text-gray-600">
                The fastest solution to sell your vehicle
              </p>
              <p className="text-gray-600">
                Selling a car online has never been easier. With over 10,000 satisfied customers, 
                our goal is to simplify the selling process and cut the time in half.
              </p>
            </div>

            <div className="p-6 bg-white rounded-lg shadow-lg">
              <h3 className="mb-4 text-xl font-semibold">We operate throughout Greater Adelaide</h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {regions.map((region, index) => (
                  <motion.div
                    key={region}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-2"
                  >
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">{region}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-lg font-medium text-gray-700">
                Join our satisfied customers today
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.dispatchEvent(scrollToCTAEvent)}
                className="px-8 py-3 text-gray-900 transition bg-[#fdc751] rounded-lg hover:bg-[#fdc751]/90 font-semibold"
              >
                Get Your Valuation Now
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 gap-8 mt-20 md:grid-cols-3">
          {[
            {
              icon: ClipboardCheck,
              title: "Enter Your Details",
              description: "Fill out the form with your vehicle details"
            },
            {
              icon: Car,
              title: "Instant Valuation",
              description: "Receive an instant valuation for your vehicle"
            },
            {
              icon: Banknote,
              title: "Complete the Sale",
              description: "Accept the offer and receive immediate payment"
            }
          ].map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className="p-6 text-center bg-white rounded-lg shadow-lg"
            >
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
                <step.icon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="mb-2 text-xl font-semibold">{step.title}</h3>
              <p className="text-gray-600">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}; 